/**
 * The new Grid's reading rules (#1766, threads/new-grid/mockup.html).
 *
 * Pure, so the whole board reads off one place and the tests pin it:
 *  - a card's COLOR is its state (decision 1), not its trade;
 *  - a tech's "left" is the tech's own remaining work — the jobs on his
 *    iPad — with office work counted separately (decision 2, #1609);
 *  - a card's TITLE leads with the site, the billing customer is a small tag
 *    (decision 3).
 */
import type { DispatchJob } from "../dispatch-types";

/** The five colors of the board. */
export type CardState = "todo" | "there" | "finished" | "office" | "person";

export const CARD_STATE_LABEL: Record<CardState, string> = {
    todo: "To do",
    there: "Tech is there",
    finished: "Finished",
    office: "Office review",
    person: "Needs a person",
};

/** The tech is on the way or on site. */
const THERE = new Set(["en_route", "arrived", "in_progress"]);
/** Nothing left for anyone on the visit. */
const FINISHED = new Set(["completed", "closed", "invoiced", "paid"]);
/** The tech is done; the office still has paperwork. */
const OFFICE = new Set(["office_review", "ready_for_invoice"]);
/** Stopped until a person in the office decides something. */
const PERSON = new Set(["held", "pending_action", "cannot_complete"]);

export type PulseRisk = "ok" | "watch" | "at_risk" | "overdue";

/**
 * Which color a job wears. Orange ("needs a person") wins over everything
 * because it is the only color that asks the office to act: a held or
 * cannot-complete job, a water-off fail protocol, or a visit the live pulse
 * says is running long / late to start.
 */
export function cardState(
    job: Pick<DispatchJob, "status" | "fail_protocol_paused">,
    risk?: PulseRisk | null,
): CardState {
    const s = (job.status ?? "").toLowerCase();
    if (PERSON.has(s) || job.fail_protocol_paused) return "person";
    if (OFFICE.has(s)) return "office";
    if (FINISHED.has(s)) return "finished";
    if (risk === "at_risk" || risk === "overdue") return "person";
    if (THERE.has(s)) return "there";
    return "todo";
}

/** Statuses that are off the board entirely. */
export function isCancelled(job: Pick<DispatchJob, "status">): boolean {
    return (job.status ?? "").toLowerCase() === "cancelled";
}

/**
 * The statuses the tech's iPad lists (the field app's Today query,
 * `client/src/pages/field/index.tsx`, and `FIELD_LIST_STATUSES` in
 * `src/server/services/techBrief/fieldServed.ts`). "Left" on the Grid is
 * exactly this set, so the Grid and the iPad can never disagree about what a
 * tech still has to do (#1609). Keep the three lists in step.
 */
export const TECH_WORK_STATUSES: ReadonlySet<string> = new Set([
    "scheduled",
    "en_route",
    "arrived",
    "in_progress",
    "pending",
    "dispatched",
    "confirmed",
]);

/** Is this job still on the tech (on his iPad, not finished, not the office's)? */
export function isTechWork(status: string | null | undefined): boolean {
    return TECH_WORK_STATUSES.has((status ?? "").toLowerCase());
}

/**
 * Decision 2 (and #1609): "N left" is what the TECH still has to do — the
 * jobs his iPad shows. Everything else that isn't finished is the office's:
 * office review, ready for invoice, held, pending action, can't complete, and
 * a job still awaiting scheduling (it sits on his row but never reaches his
 * iPad). Those count as `office` and show as "+N office" — still on the
 * board, never hidden, they just stop inflating the tech's number.
 */
export function techCounts(jobs: Array<Pick<DispatchJob, "status">>): {
    left: number;
    office: number;
    finished: number;
    total: number;
} {
    let left = 0;
    let office = 0;
    let finished = 0;
    let total = 0;
    for (const j of jobs) {
        if (isCancelled(j)) continue;
        total++;
        const s = (j.status ?? "").toLowerCase();
        if (FINISHED.has(s)) finished++;
        else if (isTechWork(s)) left++;
        else office++;
    }
    return { left, office, finished, total };
}

/**
 * The words on a tech's "left" chip. `null` total means the tech has nothing
 * on the day. Shared by the classic and the new Grid so both say the same.
 */
export function leftChipText(c: { left: number; office: number; total: number }): {
    main: string;
    office: string | null;
    title: string;
} {
    const office = c.office > 0 ? `+${c.office} office` : null;
    const main = c.left > 0 ? `${c.left} of ${c.total} left` : "Done";
    const parts = [
        `${c.left} of ${c.total} job${c.total === 1 ? "" : "s"} still on the tech`,
        c.office > 0 ? `${c.office} waiting on the office (review, invoice, hold or scheduling)` : null,
    ].filter(Boolean);
    return { main, office, title: parts.join(" · ") };
}

/**
 * Decision 3: the card says WHERE the tech is going. The site name leads; the
 * billing customer rides as a small tag only when it adds something (a site
 * named after its customer gets no tag).
 */
export function cardTitle(job: Pick<DispatchJob, "site_name" | "site_label" | "customer_name" | "title">): {
    title: string;
    customerTag: string | null;
} {
    const site = job.site_name?.trim() || job.site_label?.trim() || "";
    const customer = job.customer_name?.trim() || "";
    if (!site) return { title: customer || job.title || "Job", customerTag: null };
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "");
    const redundant = !customer || norm(site).includes(norm(customer)) || norm(customer).includes(norm(site));
    return { title: site, customerTag: redundant ? null : customer };
}

/**
 * Past due = the visit is AFTER the compliance due date, so the visit itself
 * is late. A finished job is no longer the board's problem. `visitDateKey` is
 * the board's YYYY-MM-DD.
 */
export function isPastDue(
    job: Pick<DispatchJob, "due_by_date" | "status" | "fail_protocol_paused">,
    visitDateKey: string,
): boolean {
    const due = job.due_by_date?.slice(0, 10);
    if (!due) return false;
    const state = cardState(job);
    if (state === "finished" || state === "office") return false;
    return due < visitDateKey;
}

/** Device blocks: one small block per device up to `max`, then "+N". */
export function deviceBlocks(count: number | null | undefined, max = 4): { blocks: number; more: number } {
    const n = Math.max(0, Math.floor(Number(count) || 0));
    return { blocks: Math.min(n, max), more: Math.max(0, n - max) };
}

/**
 * One plain word for what KIND of job a card is (2026-10-07, Corbin: "show
 * what kind of job it is without hovering"). Read off the job type only —
 * code first, then the name — never the free-text title, so an unknown type
 * returns null and the card shows no word rather than a guess.
 *
 * Prod job types on 2026-10-07 (/api/job-types): backflow_test,
 * backflow_repair_small/large, backflow_replace_small/large,
 * backflow_clean_flush, scope_out, fire_ext_inspection, fire_ext_service,
 * hvac_service_call, plumbing_service_call. Every word is 7 letters or fewer
 * so it fits the 132 px card floor.
 */
export type JobTypeWord =
    | "Test"
    | "Retest"
    | "Repair"
    | "Replace"
    | "Install"
    | "Flush"
    | "Inspect"
    | "Survey"
    | "Scope"
    | "Service";

const JOB_TYPE_CODE_WORD: Record<string, JobTypeWord> = {
    backflow_test: "Test",
    backflow_repair_small: "Repair",
    backflow_repair_large: "Repair",
    backflow_replace_small: "Replace",
    backflow_replace_large: "Replace",
    backflow_clean_flush: "Flush",
    scope_out: "Scope",
    fire_ext_inspection: "Inspect",
    fire_ext_service: "Service",
    hvac_service_call: "Service",
    plumbing_service_call: "Service",
};

/** Name keywords, most specific first ("retest" before "test"). */
const JOB_TYPE_NAME_WORD: Array<[RegExp, JobTypeWord]> = [
    [/\bre-?tests?\b/, "Retest"],
    [/\b(repair|rebuild)/, "Repair"],
    [/\breplac/, "Replace"],
    [/\binstall/, "Install"],
    [/\bflush/, "Flush"],
    [/\binspect/, "Inspect"],
    [/\bsurvey/, "Survey"],
    [/\bscope/, "Scope"],
    [/\btest/, "Test"],
    [/\bservice/, "Service"],
];

export function jobTypeWord(job: Pick<DispatchJob, "job_type_code" | "job_type_name">): JobTypeWord | null {
    const code = job.job_type_code?.trim().toLowerCase() ?? "";
    if (code && JOB_TYPE_CODE_WORD[code]) return JOB_TYPE_CODE_WORD[code];
    const name = `${job.job_type_name ?? ""} ${code.replace(/_/g, " ")}`.toLowerCase();
    for (const [re, word] of JOB_TYPE_NAME_WORD) if (re.test(name)) return word;
    return null;
}

/** "1 device" / "3 devices" — the spoken form of the device blocks. */
export function deviceCountLabel(count: number | null | undefined): string | null {
    const n = Math.max(0, Math.floor(Number(count) || 0));
    if (n === 0) return null;
    return `${n} device${n === 1 ? "" : "s"}`;
}

/** Points a job is worth, never NaN. */
export function jobPoints(job: Pick<DispatchJob, "computed_points">): number {
    const n = job.computed_points == null ? 0 : Number(job.computed_points);
    return Number.isFinite(n) ? n : 0;
}

/** Day-strip tallies, in the order the bar draws them. */
export const STATE_ORDER: CardState[] = ["finished", "office", "there", "todo", "person"];

export function stateTally(
    jobs: Array<Pick<DispatchJob, "id" | "status" | "fail_protocol_paused">>,
    riskOf?: (jobId: string) => PulseRisk | null | undefined,
): Record<CardState, number> {
    const out: Record<CardState, number> = { todo: 0, there: 0, finished: 0, office: 0, person: 0 };
    for (const j of jobs) {
        if (isCancelled(j)) continue;
        out[cardState(j, riskOf?.(j.id))]++;
    }
    return out;
}

/** Initials for the tech avatar. */
export function initials(first: string | null | undefined, last: string | null | undefined): string {
    const a = (first ?? "").trim()[0] ?? "";
    const b = (last ?? "").trim()[0] ?? "";
    return (a + b).toUpperCase() || "?";
}

/** "David H." */
export function shortName(first: string | null | undefined, last: string | null | undefined): string {
    const f = (first ?? "").trim();
    const l = (last ?? "").trim();
    return l ? `${f} ${l[0]}.` : f;
}

/** How many 30-minute tests fit in an open stretch, rounded down. */
export function testsThatFit(openMinutes: number): number {
    return Math.max(0, Math.floor(openMinutes / 30));
}
